import React from 'react';
import { 
    Box, 
    TextField, 
    Typography, 
    Grid, 
    IconButton
} from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Close';

// Keep COLORS constant for pie chart
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const AudienceSection = ({ 
    eventDetails, 
    handleChange,
    isEditing
}) => {
    // Initialize with 2 empty rows if no data exists
    React.useEffect(() => {
        if (!eventDetails.event.audienceInfo || eventDetails.event.audienceInfo.length === 0) {
            handleChange('audienceInfo', [
                { name: '', value: 0 },
                { name: '', value: 0 }
            ]);
        }
    }, []);

    const handleAddAudienceType = () => {
        const newAudienceData = [
            ...(eventDetails.event.audienceInfo || []),
            { name: '', value: 0 }
        ];

        handleChange('audienceInfo', newAudienceData);
    };

    const handleRemoveAudienceType = (index) => {
        const newAudienceData = [...eventDetails.event.audienceInfo];
        newAudienceData.splice(index, 1);
        handleChange('audienceInfo', newAudienceData);
    };

    const handleFieldChange = (index, field, value) => {
        const newAudienceData = [...(eventDetails.event.audienceInfo || [])];
        newAudienceData[index] = {
            ...newAudienceData[index],
            [field]: field === 'value' ? parseInt(value) || 0 : value
        };
        
        handleChange('audienceInfo', newAudienceData);
        
        // Update total guest count
        const totalGuests = newAudienceData.reduce((sum, item) => sum + (item.value || 0), 0);
        handleChange('guestCount', totalGuests);
    };

    return (
        <Box>
            <Grid container spacing={3}>
                {/* Two Column Input Section - Left Side */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        {/* Header with Examples */}
                        <Box>
                            <Box sx={{ 
                                display: 'flex', 
                                gap: 2,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                pb: 1
                            }}>
                                <Typography sx={{ flex: 1 }}>Type</Typography>
                                <Typography sx={{ flex: 1 }}>Count</Typography>
                                <Box sx={{ width: 40 }} />
                            </Box>
                            <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ mt: 0.5, display: 'block' }}
                            >
                                Examples: Founders, Investors, Students, Professionals, etc.
                            </Typography>
                        </Box>

                        {/* Audience Type Rows */}
                        {(eventDetails.event.audienceInfo || []).map((type, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2
                                }}
                            >
                                <TextField
                                    value={type.name}
                                    onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                                    disabled={!isEditing}
                                    variant="standard"
                                    sx={{ flex: 1 }}
                                    placeholder={index === 0 ? "e.g., Founders" : "e.g., Investors"}
                                />
                                <TextField
                                    value={type.value}
                                    onChange={(e) => handleFieldChange(index, 'value', e.target.value)}
                                    disabled={!isEditing}
                                    variant="standard"
                                    type="number"
                                    sx={{ flex: 1 }}
                                    placeholder="0"
                                />
                                {isEditing && eventDetails.event.audienceInfo.length > 2 && (
                                    <IconButton
                                        onClick={() => handleRemoveAudienceType(index)}
                                        sx={{
                                            color: 'error.main',
                                            '&:hover': { color: 'error.dark' }
                                        }}
                                    >
                                        <CancelIcon sx={{ color: 'gray' }} />
                                    </IconButton>
                                )}
                            </Box>
                        ))}

                        {/* Add Row Button */}
                        {isEditing && (
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center',
                                mt: 2 
                            }}>
                                <IconButton
                                    onClick={handleAddAudienceType}
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': { color: 'primary.dark' }
                                    }}
                                >
                                    <AddCircleOutlineIcon sx={{ fontSize: '2rem' }} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                </Grid>

                {/* Pie Chart - Right Side */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ 
                        width: '100%', 
                        height: 300,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={eventDetails.event.audienceInfo}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {(eventDetails.event.audienceInfo || []).map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AudienceSection; 